<template>
    <Teleport to="body">
        <Transition name="modal">
            <div v-if="show" class="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-6" @click.self="closeModal">
                <div class="bg-white rounded-xl w-full max-w-2xl max-h-[calc(100vh-48px)] flex flex-col overflow-hidden shadow-xl">
                    <!-- Header -->
                    <div class="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                        <div class="flex items-center gap-3">
                            <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center">
                                <i class="fas fa-edit text-primary text-sm"></i>
                            </div>
                            <div>
                                <h2 class="text-sm font-semibold text-gray-900">Modifier le produit</h2>
                                <p class="text-[11px] text-gray-400">Mettez à jour les informations du produit</p>
                            </div>
                        </div>
                        <button type="button" @click="closeModal" class="w-8 h-8 flex items-center justify-center rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors">
                            <i class="fas fa-times text-sm"></i>
                        </button>
                    </div>

                    <!-- Body -->
                    <div class="flex-1 overflow-y-auto">
                        <form @submit.prevent="submitForm" class="p-5 space-y-5">
                            <!-- Section: Informations -->
                            <div>
                                <div class="flex items-center gap-2 mb-4">
                                    <i class="fas fa-info-circle text-primary text-xs"></i>
                                    <span class="text-[11px] text-gray-400 uppercase tracking-wider font-medium">Informations générales</span>
                                </div>

                                <div class="grid grid-cols-[140px_1fr] gap-5">
                                    <!-- Image -->
                                    <div>
                                        <div class="aspect-square rounded-xl border-2 border-dashed border-gray-200 overflow-hidden transition-colors hover:border-primary/40"
                                            :class="{ '!border-solid !border-gray-200': imagePreview || form.image_url }">
                                            <div v-if="imagePreview || form.image_url" class="relative w-full h-full group">
                                                <img :src="imagePreview || form.image_url" alt="Aperçu" class="w-full h-full object-cover" />
                                                <div class="absolute inset-0 bg-black/40 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <button type="button" @click="removeImage" class="w-8 h-8 rounded-lg bg-white text-red-500 hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors">
                                                        <i class="fas fa-trash text-xs"></i>
                                                    </button>
                                                </div>
                                            </div>
                                            <label v-else class="flex flex-col items-center justify-center w-full h-full cursor-pointer gap-1.5 p-3">
                                                <input type="file" @change="handleImageChange" accept="image/*" class="hidden" />
                                                <i class="fas fa-image text-gray-300 text-xl"></i>
                                                <span class="text-[10px] text-gray-400 text-center">Glisser ou cliquer</span>
                                            </label>
                                        </div>
                                        <p v-if="validationErrors.image" class="text-[11px] text-red-500 mt-1">{{ validationErrors.image[0] }}</p>
                                    </div>

                                    <!-- Fields -->
                                    <div class="space-y-3">
                                        <!-- Nom -->
                                        <div>
                                            <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">Nom du produit <span class="text-red-400">*</span></label>
                                            <input v-model="form.name" type="text" placeholder="Ex: iPhone 15 Pro Max"
                                                class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                                :class="{ '!border-red-300 !bg-red-50/50': validationErrors.name }" />
                                            <p v-if="validationErrors.name" class="text-[11px] text-red-500 mt-1">{{ validationErrors.name[0] }}</p>
                                        </div>

                                        <!-- Catégorie -->
                                        <div>
                                            <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">Catégorie</label>
                                            <div class="relative" ref="categoryDropdownContainer">
                                                <button type="button" @click="toggleCategoryDropdown"
                                                    class="w-full flex items-center justify-between px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 hover:border-gray-300 transition-colors text-left">
                                                    <span :class="selectedCategoryName ? 'text-gray-900' : 'text-gray-400'">
                                                        {{ selectedCategoryName || 'Sélectionner une catégorie' }}
                                                    </span>
                                                    <i class="fas fa-chevron-down text-[10px] text-gray-400 transition-transform" :class="{ 'rotate-180': showCategoryDropdown }"></i>
                                                </button>
                                                <Transition name="dropdown">
                                                    <div v-if="showCategoryDropdown" class="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 overflow-hidden">
                                                        <div class="flex items-center gap-2 px-3 py-2 border-b border-gray-100">
                                                            <i class="fas fa-search text-gray-300 text-xs"></i>
                                                            <input v-model="searchCategory" type="text" placeholder="Rechercher..." @input="debouncedFetchCategories"
                                                                class="flex-1 text-sm bg-transparent outline-none text-gray-800 placeholder:text-gray-400" />
                                                        </div>
                                                        <div class="max-h-[160px] overflow-y-auto">
                                                            <div v-if="isSearchingCategories" class="flex items-center justify-center gap-2 py-4 text-xs text-gray-400">
                                                                <span class="w-4 h-4 border-2 border-gray-200 border-t-primary rounded-full animate-spin"></span>
                                                                Recherche...
                                                            </div>
                                                            <div v-else-if="showNewCategoryInput" class="flex items-center gap-2 p-2">
                                                                <input v-model="newCategoryName" type="text" placeholder="Nom de la catégorie" ref="newCategoryInput"
                                                                    @keyup.enter="createNewCategory" @keyup.esc="cancelNewCategory"
                                                                    class="flex-1 px-2.5 py-1.5 text-sm border border-gray-200 rounded-lg outline-none focus:border-primary" />
                                                                <button type="button" @click="cancelNewCategory" :disabled="isCreatingCategory"
                                                                    class="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:bg-gray-100 disabled:opacity-50">
                                                                    <i class="fas fa-times text-xs"></i>
                                                                </button>
                                                                <button type="button" @click="createNewCategory" :disabled="!newCategoryName.trim() || isCreatingCategory"
                                                                    class="w-7 h-7 flex items-center justify-center rounded-md bg-primary text-white hover:bg-primary-dark disabled:opacity-50">
                                                                    <span v-if="isCreatingCategory" class="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                                                    <i v-else class="fas fa-check text-xs"></i>
                                                                </button>
                                                            </div>
                                                            <ul v-else-if="filteredCategories.length" class="p-1">
                                                                <li v-for="category in filteredCategories" :key="category.id" @click="selectCategory(category)"
                                                                    class="flex items-center justify-between px-2.5 py-2 text-sm rounded-md cursor-pointer transition-colors"
                                                                    :class="form.category_id === category.id ? 'bg-primary/5 text-primary font-medium' : 'text-gray-700 hover:bg-gray-50'">
                                                                    <span>{{ category.name }}</span>
                                                                    <i v-if="form.category_id === category.id" class="fas fa-check text-[10px]"></i>
                                                                </li>
                                                            </ul>
                                                            <p v-else class="text-center text-xs text-gray-400 py-4">Aucune catégorie trouvée</p>
                                                        </div>
                                                        <button type="button" @click="prepareNewCategory"
                                                            class="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-xs font-medium text-primary bg-gray-50 border-t border-gray-100 hover:bg-primary/5 transition-colors">
                                                            <i class="fas fa-plus text-[10px]"></i>
                                                            Créer une catégorie
                                                        </button>
                                                    </div>
                                                </Transition>
                                            </div>
                                            <p v-if="validationErrors.category_id" class="text-[11px] text-red-500 mt-1">{{ validationErrors.category_id[0] }}</p>
                                        </div>

                                        <!-- Code-barres & SKU -->
                                        <div class="grid grid-cols-2 gap-3">
                                            <div>
                                                <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">Code-barres</label>
                                                <input v-model="form.barcode" type="text" placeholder="1234567890123" maxlength="100"
                                                    class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                                    :class="{ '!border-red-300 !bg-red-50/50': validationErrors.barcode }" />
                                                <p v-if="validationErrors.barcode" class="text-[11px] text-red-500 mt-1">{{ validationErrors.barcode[0] }}</p>
                                            </div>
                                            <div>
                                                <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">SKU</label>
                                                <input v-model="form.sku" type="text" placeholder="IPH-15-PM-256" maxlength="100"
                                                    class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                                    :class="{ '!border-red-300 !bg-red-50/50': validationErrors.sku }" />
                                                <p v-if="validationErrors.sku" class="text-[11px] text-red-500 mt-1">{{ validationErrors.sku[0] }}</p>
                                            </div>
                                        </div>

                                        <!-- Référence -->
                                        <div>
                                            <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">Référence</label>
                                            <input v-model="form.reference" type="text" placeholder="REF-2024-001" maxlength="100"
                                                class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                                :class="{ '!border-red-300 !bg-red-50/50': validationErrors.reference }" />
                                            <p v-if="validationErrors.reference" class="text-[11px] text-red-500 mt-1">{{ validationErrors.reference[0] }}</p>
                                        </div>
                                    </div>
                                </div>

                                <!-- Description -->
                                <div class="mt-3">
                                    <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">Description</label>
                                    <textarea v-model="form.description" placeholder="Décrivez votre produit..." rows="2"
                                        class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all resize-y min-h-[60px]"
                                        :class="{ '!border-red-300 !bg-red-50/50': validationErrors.description }"></textarea>
                                    <p v-if="validationErrors.description" class="text-[11px] text-red-500 mt-1">{{ validationErrors.description[0] }}</p>
                                </div>
                            </div>

                            <!-- Section: Tarification -->
                            <div>
                                <div class="flex items-center gap-2 mb-4">
                                    <i class="fas fa-tag text-primary text-xs"></i>
                                    <span class="text-[11px] text-gray-400 uppercase tracking-wider font-medium">Tarification</span>
                                </div>

                                <div class="grid grid-cols-3 gap-3">
                                    <!-- Prix HT -->
                                    <div class="bg-gray-50/80 rounded-xl p-3.5 border border-gray-100">
                                        <div class="flex items-center gap-2 mb-2.5">
                                            <div class="w-7 h-7 rounded-lg bg-primary/10 flex items-center justify-center">
                                                <i class="fas fa-coins text-primary text-[10px]"></i>
                                            </div>
                                            <span class="text-[11px] text-gray-500 font-medium">Prix HT <span class="text-red-400">*</span></span>
                                        </div>
                                        <div class="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary">
                                            <input v-model="form.price_excl_tax" type="number" min="0" step="50" placeholder="0"
                                                class="flex-1 px-3 py-2 text-sm font-semibold text-gray-900 bg-transparent outline-none w-full"
                                                :class="{ '!border-red-300': validationErrors.price_excl_tax }" />
                                            <span class="px-2.5 py-2 text-[11px] text-gray-400 bg-gray-50 font-medium">FCFA</span>
                                        </div>
                                        <p v-if="validationErrors.price_excl_tax" class="text-[11px] text-red-500 mt-1">{{ validationErrors.price_excl_tax[0] }}</p>
                                    </div>

                                    <!-- TVA -->
                                    <div class="bg-gray-50/80 rounded-xl p-3.5 border border-gray-100">
                                        <div class="flex items-center gap-2 mb-2.5">
                                            <div class="w-7 h-7 rounded-lg bg-violet-50 flex items-center justify-center">
                                                <i class="fas fa-percent text-violet-500 text-[10px]"></i>
                                            </div>
                                            <span class="text-[11px] text-gray-500 font-medium">Taux TVA <span class="text-red-400">*</span></span>
                                        </div>
                                        <div class="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden focus-within:ring-2 focus-within:ring-primary/20 focus-within:border-primary">
                                            <input v-model="form.tax_rate" type="number" min="0" max="100" step="1" placeholder="18"
                                                class="flex-1 px-3 py-2 text-sm font-semibold text-gray-900 bg-transparent outline-none w-full"
                                                :class="{ '!border-red-300': validationErrors.tax_rate }" />
                                            <span class="px-2.5 py-2 text-[11px] text-gray-400 bg-gray-50 font-medium">%</span>
                                        </div>
                                        <p v-if="validationErrors.tax_rate" class="text-[11px] text-red-500 mt-1">{{ validationErrors.tax_rate[0] }}</p>
                                    </div>

                                    <!-- Prix TTC -->
                                    <div class="bg-emerald-50/60 rounded-xl p-3.5 border border-emerald-100">
                                        <div class="flex items-center gap-2 mb-2.5">
                                            <div class="w-7 h-7 rounded-lg bg-emerald-100 flex items-center justify-center">
                                                <i class="fas fa-receipt text-emerald-600 text-[10px]"></i>
                                            </div>
                                            <span class="text-[11px] text-gray-500 font-medium">Prix TTC</span>
                                        </div>
                                        <div class="flex items-baseline gap-1.5">
                                            <span class="text-lg font-bold text-gray-900">{{ computedPriceTTC }}</span>
                                            <span class="text-[11px] text-gray-400 font-medium">FCFA</span>
                                        </div>
                                        <p class="text-[10px] text-gray-400 mt-1">Calculé automatiquement</p>
                                    </div>
                                </div>
                            </div>

                            <!-- Section: Stock -->
                            <div>
                                <div class="flex items-center justify-between mb-4">
                                    <div class="flex items-center gap-2">
                                        <i class="fas fa-box text-primary text-xs"></i>
                                        <span class="text-[11px] text-gray-400 uppercase tracking-wider font-medium">Stock</span>
                                    </div>
                                    <!-- Toggle inventaire -->
                                    <label class="flex items-center gap-2 cursor-pointer">
                                        <span class="text-xs text-gray-500">Suivre l'inventaire</span>
                                        <div class="relative">
                                            <input type="checkbox" v-model="form.track_inventory" class="sr-only peer" />
                                            <div class="w-9 h-5 bg-gray-200 rounded-full peer-checked:bg-primary transition-colors"></div>
                                            <div class="absolute left-0.5 top-0.5 w-4 h-4 bg-white rounded-full shadow-sm transition-transform peer-checked:translate-x-4"></div>
                                        </div>
                                    </label>
                                </div>

                                <!-- Unité de mesure -->
                                <div class="mb-3">
                                    <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-1 block">Unité de mesure</label>
                                    <input v-model="form.unit" type="text" placeholder="Ex: pièce, kg, litre..." maxlength="50"
                                        class="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50/50 focus:bg-white focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
                                        :class="{ '!border-red-300 !bg-red-50/50': validationErrors.unit }" />
                                    <p v-if="validationErrors.unit" class="text-[11px] text-red-500 mt-1">{{ validationErrors.unit[0] }}</p>
                                </div>

                                <div class="grid grid-cols-3 gap-3">
                                    <!-- Stock actuel -->
                                    <div class="bg-gray-50/80 rounded-xl p-3.5 border border-gray-100">
                                        <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2 block">Stock actuel</label>
                                        <div class="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden w-full">
                                            <button type="button" @click="adjustStock('stock_quantity', -1)"
                                                class="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                                                <i class="fas fa-minus text-xs"></i>
                                            </button>
                                            <input v-model.number="form.stock_quantity" type="number" min="0"
                                                class="flex-1 h-9 text-center text-sm font-semibold text-gray-900 border-x border-gray-200 bg-transparent outline-none" />
                                            <button type="button" @click="adjustStock('stock_quantity', 1)"
                                                class="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-primary hover:bg-primary/5 transition-colors">
                                                <i class="fas fa-plus text-xs"></i>
                                            </button>
                                        </div>
                                        <p v-if="validationErrors.stock_quantity" class="text-[11px] text-red-500 mt-1">{{ validationErrors.stock_quantity[0] }}</p>
                                    </div>

                                    <!-- Stock minimum -->
                                    <Transition name="fade">
                                        <div v-if="form.track_inventory" class="bg-amber-50/60 rounded-xl p-3.5 border border-amber-100">
                                            <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2 block">Stock minimum</label>
                                            <div class="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden w-full">
                                                <button type="button" @click="adjustStock('minimum_stock', -1)"
                                                    class="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                                                    <i class="fas fa-minus text-xs"></i>
                                                </button>
                                                <input v-model.number="form.minimum_stock" type="number" min="0" placeholder="0"
                                                    class="flex-1 h-9 text-center text-sm font-semibold text-gray-900 border-x border-gray-200 bg-transparent outline-none" />
                                                <button type="button" @click="adjustStock('minimum_stock', 1)"
                                                    class="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-primary hover:bg-primary/5 transition-colors">
                                                    <i class="fas fa-plus text-xs"></i>
                                                </button>
                                            </div>
                                            <p class="text-[10px] text-gray-400 mt-1.5">Alerte si stock &le; cette valeur</p>
                                        </div>
                                    </Transition>

                                    <!-- Stock maximum -->
                                    <Transition name="fade">
                                        <div v-if="form.track_inventory" class="bg-emerald-50/60 rounded-xl p-3.5 border border-emerald-100">
                                            <label class="text-[11px] text-gray-400 uppercase tracking-wider font-medium mb-2 block">Stock maximum</label>
                                            <div class="flex items-center bg-white border border-gray-200 rounded-lg overflow-hidden w-full">
                                                <button type="button" @click="adjustStock('maximum_stock', -1)"
                                                    class="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-red-500 hover:bg-red-50 transition-colors">
                                                    <i class="fas fa-minus text-xs"></i>
                                                </button>
                                                <input v-model.number="form.maximum_stock" type="number" min="0" placeholder="0"
                                                    class="flex-1 h-9 text-center text-sm font-semibold text-gray-900 border-x border-gray-200 bg-transparent outline-none" />
                                                <button type="button" @click="adjustStock('maximum_stock', 1)"
                                                    class="w-9 h-9 flex items-center justify-center text-gray-400 hover:text-primary hover:bg-primary/5 transition-colors">
                                                    <i class="fas fa-plus text-xs"></i>
                                                </button>
                                            </div>
                                            <p class="text-[10px] text-gray-400 mt-1.5">Capacité maximale</p>
                                        </div>
                                    </Transition>
                                </div>

                                <!-- Stock Indicator Bar -->
                                <Transition name="fade">
                                    <div v-if="form.track_inventory && form.maximum_stock > 0" class="mt-3 bg-gray-50/80 rounded-xl p-4 border border-gray-100">
                                        <div class="h-2 bg-gray-200 rounded-full overflow-visible relative">
                                            <div class="h-full rounded-full transition-all duration-400"
                                                :style="{ width: stockPercentage + '%' }"
                                                :class="{
                                                    'bg-gradient-to-r from-primary to-teal-400': stockStatus === 'good' || stockStatus === 'normal',
                                                    'bg-gradient-to-r from-amber-400 to-amber-300': stockStatus === 'warning',
                                                    'bg-gradient-to-r from-red-500 to-red-400': stockStatus === 'critical'
                                                }"></div>
                                            <div v-if="form.minimum_stock" class="absolute top-[-4px]"
                                                :style="{ left: minimumStockPercentage + '%' }">
                                                <div class="w-0.5 h-[18px] bg-amber-400 rounded-full -translate-x-1/2"></div>
                                                <span class="text-[9px] font-semibold text-amber-500 uppercase -translate-x-1/2 block mt-0.5">Min</span>
                                            </div>
                                        </div>
                                        <div class="flex items-center justify-between mt-2.5 text-xs">
                                            <span class="text-gray-500">{{ form.stock_quantity || 0 }} / {{ form.maximum_stock }} unités</span>
                                            <span class="font-medium"
                                                :class="{
                                                    'text-emerald-600': stockStatus === 'good',
                                                    'text-amber-600': stockStatus === 'warning',
                                                    'text-red-600': stockStatus === 'critical'
                                                }">{{ stockStatusText }}</span>
                                        </div>
                                    </div>
                                </Transition>
                            </div>
                        </form>
                    </div>

                    <!-- Footer -->
                    <div class="flex items-center justify-between px-5 py-3.5 border-t border-gray-100 bg-gray-50/50">
                        <span class="text-[10px] text-gray-400">* Champs obligatoires</span>
                        <div class="flex items-center gap-2">
                            <button type="button" @click="closeModal"
                                class="inline-flex items-center gap-1.5 px-3.5 py-2 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 hover:border-gray-300 transition-colors">
                                Annuler
                            </button>
                            <button type="button" @click="submitForm" :disabled="isSubmitting"
                                class="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-medium text-white bg-primary rounded-lg hover:bg-primary-dark disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
                                <span v-if="isSubmitting" class="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin"></span>
                                <template v-else>
                                    <i class="fas fa-check text-[10px]"></i>
                                    Mettre à jour
                                </template>
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script>
import { useToastStore } from '@/store/modules';
import { categoriesService } from '@/services';
import { ref, onMounted, onBeforeUnmount, nextTick } from 'vue';

export default {
    name: 'EditProductModal',
    props: {
        show: {
            type: Boolean,
            default: false
        },
        product: {
            type: Object,
            required: true
        },
        categories: {
            type: Array,
            default: () => []
        }
    },
    setup() {
        const toastStore = useToastStore();
        const categoryDropdownContainer = ref(null);
        const showNewCategoryInput = ref(false);
        const newCategoryName = ref('');
        const isCreatingCategory = ref(false);
        const showCategoryDropdown = ref(false);

        const handleClickOutside = (event) => {
            if (categoryDropdownContainer.value && !categoryDropdownContainer.value.contains(event.target)) {
                showCategoryDropdown.value = false;
            }
        };

        onMounted(() => {
            document.addEventListener('click', handleClickOutside);
        });

        onBeforeUnmount(() => {
            document.removeEventListener('click', handleClickOutside);
        });

        return {
            toastStore,
            categoryDropdownContainer,
            showNewCategoryInput,
            newCategoryName,
            isCreatingCategory,
            showCategoryDropdown
        };
    },
    data() {
        return {
            form: {
                name: '',
                description: '',
                sku: '',
                barcode: '',
                reference: '',
                price_excl_tax: '',
                tax_rate: 0,
                category_id: '',
                stock_quantity: 0,
                minimum_stock: null,
                maximum_stock: null,
                unit: '',
                track_inventory: true,
                image: null,
                image_url: null
            },
            imagePreview: null,
            validationErrors: {},
            isSubmitting: false,
            availableCategories: [],
            searchCategory: '',
            isSearchingCategories: false,
            selectedCategoryName: '',
            fetchCategoriesTimeout: null
        };
    },
    computed: {
        filteredCategories() {
            if (!this.searchCategory.trim()) {
                return this.availableCategories;
            }
            const searchTerm = this.searchCategory.toLowerCase().trim();
            return this.availableCategories.filter(
                category => category.name.toLowerCase().includes(searchTerm)
            );
        },
        computedPriceTTC() {
            const priceHT = parseFloat(this.form.price_excl_tax) || 0;
            const taxRate = parseFloat(this.form.tax_rate) || 0;
            const priceTTC = priceHT * (1 + taxRate / 100);
            return new Intl.NumberFormat('fr-FR').format(Math.round(priceTTC));
        },
        stockPercentage() {
            if (!this.form.maximum_stock) return 0;
            return Math.min(100, (this.form.stock_quantity / this.form.maximum_stock) * 100);
        },
        minimumStockPercentage() {
            if (!this.form.maximum_stock || !this.form.minimum_stock) return 0;
            return (this.form.minimum_stock / this.form.maximum_stock) * 100;
        },
        stockStatus() {
            if (!this.form.minimum_stock) return 'normal';
            if (this.form.stock_quantity <= this.form.minimum_stock) return 'critical';
            if (this.form.stock_quantity <= this.form.minimum_stock * 1.5) return 'warning';
            return 'good';
        },
        stockStatusText() {
            switch (this.stockStatus) {
                case 'critical': return 'Stock critique';
                case 'warning': return 'Stock bas';
                case 'good': return 'Stock optimal';
                default: return '';
            }
        }
    },
    watch: {
        show(newVal) {
            if (newVal) {
                this.initForm();
                this.fetchCategories();
                document.body.style.overflow = 'hidden';
            } else {
                document.body.style.overflow = '';
            }
        },
        product: {
            handler(newVal) {
                if (this.show && newVal) {
                    this.initForm();
                }
            },
            deep: true
        }
    },
    methods: {
        initForm() {
            if (!this.product) return;

            this.form = {
                name: this.product.name || '',
                description: this.product.description || '',
                sku: this.product.sku || '',
                barcode: this.product.barcode || '',
                reference: this.product.reference || '',
                price_excl_tax: this.product.price_excl_tax || '',
                tax_rate: this.product.tax_rate || 0,
                category_id: this.product.category_id || '',
                stock_quantity: this.product.stock_quantity || 0,
                minimum_stock: this.product.minimum_stock || null,
                maximum_stock: this.product.maximum_stock || null,
                unit: this.product.unit || '',
                track_inventory: this.product.track_inventory !== undefined ? this.product.track_inventory : true,
                image: null,
                image_url: this.product.image_url || null
            };

            if (this.form.category_id && this.categories.length > 0) {
                const category = this.categories.find(c => c.id === this.form.category_id);
                if (category) {
                    this.selectedCategoryName = category.name;
                    this.searchCategory = category.name;
                }
            } else {
                this.loadSelectedCategoryDetails();
            }

            this.imagePreview = this.product.image_url || null;
            this.validationErrors = {};
        },

        async loadSelectedCategoryDetails() {
            if (this.form.category_id) {
                try {
                    const response = await categoriesService.getCategory(this.form.category_id);
                    if (response && response.data) {
                        this.selectedCategoryName = response.data.name;
                        this.searchCategory = response.data.name;
                    }
                } catch (error) {
                    console.error('Erreur lors du chargement des détails de la catégorie:', error);
                }
            }
        },

        toggleCategoryDropdown() {
            this.showCategoryDropdown = !this.showCategoryDropdown;
        },
        debouncedFetchCategories() {
            clearTimeout(this.fetchCategoriesTimeout);
            this.fetchCategoriesTimeout = setTimeout(() => {
                this.fetchCategories();
            }, 300);
        },
        prepareNewCategory() {
            this.showNewCategoryInput = true;
            this.newCategoryName = this.searchCategory;
            nextTick(() => {
                if (this.$refs.newCategoryInput) {
                    this.$refs.newCategoryInput.focus();
                }
            });
        },
        cancelNewCategory() {
            this.showNewCategoryInput = false;
            this.newCategoryName = '';
        },
        async createNewCategory() {
            if (!this.newCategoryName.trim()) return;
            this.isCreatingCategory = true;
            try {
                const response = await categoriesService.createCategory({
                    name: this.newCategoryName.trim()
                });
                const newCategory = response.data.category || response.data;
                this.availableCategories.push(newCategory);
                this.form.category_id = newCategory.id;
                this.selectedCategoryName = newCategory.name;
                this.searchCategory = newCategory.name;
                this.toastStore.addToast({
                    type: 'success',
                    message: 'Catégorie créée avec succès!'
                });
                this.showNewCategoryInput = false;
                this.newCategoryName = '';
                this.showCategoryDropdown = false;
            } catch (error) {
                console.error('Erreur lors de la création de la catégorie:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de la création de la catégorie.',
                    duration: 5000
                });
            } finally {
                this.isCreatingCategory = false;
            }
        },
        async fetchCategories() {
            this.isSearchingCategories = true;
            try {
                const response = await categoriesService.getCategories({
                    perPage: 20,
                    page: 1,
                    search: this.searchCategory
                });
                if (response && response.data) {
                    this.availableCategories = response.data.data || response.data;
                }
            } catch (error) {
                console.error('Erreur lors de la récupération des catégories:', error);
                this.toastStore.addToast({
                    type: 'error',
                    message: 'Erreur lors de la récupération des catégories.',
                    duration: 6000
                });
            } finally {
                this.isSearchingCategories = false;
            }
        },
        selectCategory(category) {
            this.form.category_id = category.id;
            this.selectedCategoryName = category.name;
            this.searchCategory = category.name;
            this.showCategoryDropdown = false;
        },
        handleImageChange(event) {
            const file = event.target.files[0];
            if (!file) return;
            if (file.size > 2 * 1024 * 1024) {
                this.validationErrors.image = ['L\'image ne doit pas dépasser 2 Mo'];
                return;
            }
            this.form.image = file;
            this.validationErrors.image = null;
            const reader = new FileReader();
            reader.onload = (e) => {
                this.imagePreview = e.target.result;
            };
            reader.readAsDataURL(file);
        },
        removeImage() {
            this.form.image = null;
            this.form.image_url = null;
            this.imagePreview = null;
        },
        adjustStock(field, delta) {
            const current = this.form[field] || 0;
            this.form[field] = Math.max(0, current + delta);
        },
        validateForm() {
            let isValid = true;
            this.validationErrors = {};

            if (!this.form.name) {
                this.validationErrors.name = ['Le nom du produit est requis'];
                isValid = false;
            } else if (this.form.name.length > 255) {
                this.validationErrors.name = ['Le nom ne doit pas dépasser 255 caractères'];
                isValid = false;
            }

            if (!this.form.price_excl_tax) {
                this.validationErrors.price_excl_tax = ['Le prix HT est requis'];
                isValid = false;
            } else if (isNaN(parseFloat(this.form.price_excl_tax)) || parseFloat(this.form.price_excl_tax) < 0) {
                this.validationErrors.price_excl_tax = ['Le prix doit être un nombre positif'];
                isValid = false;
            }

            if (this.form.tax_rate === '' || this.form.tax_rate === null) {
                this.validationErrors.tax_rate = ['Le taux de TVA est requis'];
                isValid = false;
            } else if (
                isNaN(parseFloat(this.form.tax_rate)) ||
                parseFloat(this.form.tax_rate) < 0 ||
                parseFloat(this.form.tax_rate) > 100
            ) {
                this.validationErrors.tax_rate = ['Le taux de TVA doit être entre 0 et 100'];
                isValid = false;
            }

            return isValid;
        },
        submitForm() {
            if (!this.validateForm()) return;

            this.isSubmitting = true;
            const formData = { ...this.form };

            if (!formData.sku?.trim()) formData.sku = null;
            if (!formData.barcode?.trim()) formData.barcode = null;
            if (!formData.reference?.trim()) formData.reference = null;
            if (!formData.description?.trim()) formData.description = null;
            if (!formData.unit?.trim()) formData.unit = null;
            if (!formData.category_id) formData.category_id = null;
            if (formData.minimum_stock === null || formData.minimum_stock === '') formData.minimum_stock = null;
            if (formData.maximum_stock === null || formData.maximum_stock === '') formData.maximum_stock = null;
            if (formData.stock_quantity === null || formData.stock_quantity === '') formData.stock_quantity = null;
            formData.track_inventory = Boolean(formData.track_inventory);

            this.$emit('submit', formData);
        },
        closeModal() {
            this.$emit('close');
        }
    }
};
</script>

<style scoped>
.modal-enter-active,
.modal-leave-active {
    transition: opacity 0.25s ease;
}
.modal-enter-active > div,
.modal-leave-active > div {
    transition: transform 0.25s ease, opacity 0.25s ease;
}
.modal-enter-from,
.modal-leave-to {
    opacity: 0;
}
.modal-enter-from > div,
.modal-leave-to > div {
    transform: scale(0.95) translateY(20px);
    opacity: 0;
}
.dropdown-enter-active,
.dropdown-leave-active {
    transition: all 0.2s ease;
}
.dropdown-enter-from,
.dropdown-leave-to {
    opacity: 0;
    transform: translateY(-6px);
}
.fade-enter-active,
.fade-leave-active {
    transition: all 0.3s ease;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}
input[type="number"]::-webkit-outer-spin-button,
input[type="number"]::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
}
input[type="number"] {
    -moz-appearance: textfield;
}
</style>
